// src/pages/StoresPage.jsx
import React, { useState, useEffect } from 'react';
import {
  Store, Plus, Edit2, Phone, MapPin,
  CheckCircle, Building2, Percent
} from 'lucide-react';
import { useApp } from '../context/AppContext';
import { translations } from '../utils/translations';
import { storeApi, shopSettingsApi } from '../api';
import { decode as decodePlusCode, encode as encodePlusCode } from 'pluscodes';

// 额外语言字段配置——和 ProductDialog.jsx、CategoryDialog.jsx 用的是同一套
// 语言代码/标签，保持整个系统"哪个代码对应哪种语言"这件事统一。
// label 现在按 $t() 动态取，不再写死中文
const LANGUAGE_FIELD_CONFIG = [
  { code: 'en',    field: 'name_en',    labelKey: 'langEnglish' },
  { code: 'km',    field: 'name_km',    labelKey: 'langKhmer' },
  { code: 'ms',    field: 'name_ms',    labelKey: 'langMalay' },
  { code: 'zh_tw', field: 'name_zh_tw', labelKey: 'langTraditionalChinese' },
];

// 按当前POS界面语言，取门店名称对应的字段——某个语言没填翻译，自动退回中文
const STORE_NAME_FIELD_MAP = { zh: 'name', en: 'name_en', km: 'name_km', ms: 'name_ms', zh_tw: 'name_zh_tw' };
function getStoreName(store, language) {
  if (!store) return '';
  const field = STORE_NAME_FIELD_MAP[language] || 'name';
  return store[field] || store.name;
}

const StoresPage = () => {
  const { language } = useApp();
  const t = translations[language];

  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingStore, setEditingStore] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const emptyForm = {
    name: '', name_en: '', name_km: '', name_ms: '', name_zh_tw: '',
    address: '', phone: '', status: 'active', commission_rate: '5',
    plus_code: '', supports_pickup: true
  };
  const [form, setForm] = useState(emptyForm);

  // 额外语言输入框：只显示商家设置里"启用的语言"里有的那几个，
  // 和 ProductDialog.jsx/CategoryDialog.jsx 用的是同一套逻辑
  const [enabledLangCodes, setEnabledLangCodes] = useState(['en', 'km']); // 加载完设置前的默认兜底
  useEffect(() => {
    shopSettingsApi.getSettings().then(res => {
      if (res.success && res.data && Array.isArray(res.data.enabled_languages)) {
        setEnabledLangCodes(res.data.enabled_languages.filter(c => c !== 'zh'));
      }
    }).catch(() => {}); // 加载失败就用上面的默认兜底，不影响表单基本可用
  }, []);
  const visibleLangFields = LANGUAGE_FIELD_CONFIG.filter(l => enabledLangCodes.includes(l.code));

  useEffect(() => { loadStores(); }, []);

  const loadStores = async () => {
    setLoading(true);
    try {
      const res = await storeApi.getList();
      if (res.success) setStores(res.data);
    } catch (err) {
      console.error('加载门店失败:', err);
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => {
    setEditingStore(null);
    setForm(emptyForm);
    setError('');
    setShowModal(true);
  };

  const openEdit = (store) => {
    setEditingStore(store);
    // 已有经纬度的话，反向编码成Plus Code显示出来，方便店长看到"现在存的是这个位置"，
    // 不用凭空猜之前录入的到底对不对；编码失败（比如经纬度数据本身有问题）就留空，
    // 不阻塞正常打开编辑弹窗
    let plusCode = '';
    if (store.latitude != null && store.longitude != null) {
      try {
        plusCode = encodePlusCode({ latitude: parseFloat(store.latitude), longitude: parseFloat(store.longitude) });
      } catch (e) {
        console.error('经纬度转Plus Code失败:', e);
      }
    }
    setForm({
      name: store.name || '',
      name_en: store.name_en || '',
      name_km: store.name_km || '',
      name_ms: store.name_ms || '',
      name_zh_tw: store.name_zh_tw || '',
      address: store.address || '',
      phone: store.phone || '',
      status: store.status || 'active',
      commission_rate: store.commission_rate ? (parseFloat(store.commission_rate) * 100).toFixed(1) : '5',
      plus_code: plusCode,
      supports_pickup: store.supports_pickup !== undefined ? !!store.supports_pickup : true
    });
    setError('');
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) { setError(t.errNameRequired); return; }
    const rate = parseFloat(form.commission_rate);
    if (isNaN(rate) || rate < 0 || rate > 100) { setError(t.errRateRange); return; }

    // Plus Code 转经纬度——只有填了才转换，留空就不带这两个字段（后端 COALESCE 会保留原值，
    // 不会把已经存好的位置清空）。转换失败（格式不对）直接拦下，不静默忽略，
    // 不然店长会以为保存成功了、其实位置根本没存对
    let latitude, longitude;
    if (form.plus_code && form.plus_code.trim()) {
      try {
        const decoded = decodePlusCode(form.plus_code.trim());
        latitude = decoded.latitude;
        longitude = decoded.longitude;
      } catch (e) {
        setError(t.errInvalidPlusCode);
        return;
      }
    }

    setSaving(true);
    setError('');
    try {
      const { plus_code, ...restForm } = form; // plus_code 只是输入方式，不是真正要发给后端的字段
      const payload = {
        ...restForm,
        commission_rate: (rate / 100).toFixed(4),  // 转为小数存储，如 5% → 0.0500
        supports_pickup: restForm.supports_pickup ? 1 : 0,
        ...(latitude != null ? { latitude, longitude } : {})
      };
      let res;
      if (editingStore) {
        res = await storeApi.update(editingStore.id, payload);
      } else {
        res = await storeApi.create({ ...payload, type: 'branch' });
      }
      if (res.success) { setShowModal(false); loadStores(); }
      else setError(res.message || t.errOperationFailed);
    } catch (err) {
      setError(err.message || t.errOperationFailed);
    } finally {
      setSaving(false);
    }
  };

  const headquarters = stores.find(s => s.type === 'headquarters');
  const branches = stores.filter(s => s.type === 'branch');
  const isBranch = editingStore?.type === 'branch' || !editingStore;

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
          <p className="text-gray-600">{t.loadingStoresText}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* 头部 */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold">{t.storesTitle}</h2>
          <p className="text-gray-600 mt-1">{t.storesSubtitle}</p>
        </div>
        <button
          onClick={openCreate}
          className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Plus className="w-4 h-4" />
          <span>{t.addBranchBtn}</span>
        </button>
      </div>

      {/* 统计卡片 */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white rounded-xl shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm">{t.totalStoresLabel}</p>
              <p className="text-3xl font-bold mt-2">{stores.length}</p>
            </div>
            <div className="bg-blue-100 p-3 rounded-full">
              <Building2 className="w-8 h-8 text-blue-600" />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm">{t.branchCountLabel}</p>
              <p className="text-3xl font-bold mt-2">{branches.length}</p>
            </div>
            <div className="bg-purple-100 p-3 rounded-full">
              <Store className="w-8 h-8 text-purple-600" />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm">{t.activeStatus}</p>
              <p className="text-3xl font-bold mt-2">{stores.filter(s => s.status === 'active').length}</p>
            </div>
            <div className="bg-green-100 p-3 rounded-full">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
          </div>
        </div>
      </div>

      {/* 总店 */}
      {headquarters && (
        <div>
          <h3 className="text-lg font-semibold mb-3 flex items-center space-x-2">
            <Building2 className="w-5 h-5 text-blue-600" />
            <span>{t.headquartersLabel}</span>
          </h3>
          <div className="bg-white rounded-xl shadow border-l-4 border-blue-600 p-6">
            <div className="flex items-start justify-between">
              <div className="flex items-start space-x-4">
                <div className="bg-blue-100 p-3 rounded-full">
                  <Building2 className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <div className="flex items-center space-x-2">
                    <h4 className="text-lg font-bold">{getStoreName(headquarters, language)}</h4>
                    <span className="bg-blue-100 text-blue-600 text-xs px-2 py-0.5 rounded-full">{t.headquartersLabel}</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${headquarters.status === 'active' ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-500'}`}>
                      {headquarters.status === 'active' ? t.activeStatus : t.closedStatus}
                    </span>
                  </div>
                  {headquarters.name_en && <p className="text-gray-500 text-sm mt-0.5">{headquarters.name_en}</p>}
                  <div className="flex items-center space-x-4 mt-2 text-sm text-gray-600">
                    {headquarters.address && (
                      <span className="flex items-center space-x-1">
                        <MapPin className="w-4 h-4" /><span>{headquarters.address}</span>
                      </span>
                    )}
                    {headquarters.phone && (
                      <span className="flex items-center space-x-1">
                        <Phone className="w-4 h-4" /><span>{headquarters.phone}</span>
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <button onClick={() => openEdit(headquarters)}
                className="flex items-center space-x-1 text-gray-500 hover:text-blue-600 transition-colors px-3 py-1.5 rounded-lg hover:bg-blue-50">
                <Edit2 className="w-4 h-4" /><span className="text-sm">{t.edit}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 分店列表 */}
      <div>
        <h3 className="text-lg font-semibold mb-3 flex items-center space-x-2">
          <Store className="w-5 h-5 text-purple-600" />
          <span>{t.branchListLabel}</span>
          <span className="bg-gray-100 text-gray-600 text-sm px-2 py-0.5 rounded-full">{branches.length}</span>
        </h3>

        {branches.length === 0 ? (
          <div className="bg-white rounded-xl shadow p-12 text-center">
            <Store className="w-16 h-16 mx-auto mb-4 text-gray-300" />
            <p className="text-gray-500 text-lg">{t.noBranchesText}</p>
            <button onClick={openCreate} className="mt-4 bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700">
              {t.addBranchBtn}
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {branches.map(store => (
              <div key={store.id} className="bg-white rounded-xl shadow p-6">
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-4">
                    <div className="bg-purple-100 p-3 rounded-full">
                      <Store className="w-6 h-6 text-purple-600" />
                    </div>
                    <div>
                      <div className="flex items-center space-x-2">
                        <h4 className="font-bold">{getStoreName(store, language)}</h4>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${store.status === 'active' ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-500'}`}>
                          {store.status === 'active' ? t.activeStatus : t.closedStatus}
                        </span>
                      </div>
                      {store.name_en && <p className="text-gray-500 text-sm mt-0.5">{store.name_en}</p>}
                      <div className="mt-2 space-y-1 text-sm text-gray-600">
                        {store.address && (
                          <p className="flex items-center space-x-1">
                            <MapPin className="w-3.5 h-3.5" /><span>{store.address}</span>
                          </p>
                        )}
                        {store.phone && (
                          <p className="flex items-center space-x-1">
                            <Phone className="w-3.5 h-3.5" /><span>{store.phone}</span>
                          </p>
                        )}
                        {/* 佣金比例显示 */}
                        <p className="flex items-center space-x-1 text-orange-600 font-medium">
                          <Percent className="w-3.5 h-3.5" />
                          <span>{t.managementFeeLabel.replace('{percent}', store.commission_rate ? (parseFloat(store.commission_rate) * 100).toFixed(1) : '5.0')}</span>
                        </p>
                      </div>
                    </div>
                  </div>
                  <button onClick={() => openEdit(store)}
                    className="flex items-center space-x-1 text-gray-500 hover:text-blue-600 transition-colors px-3 py-1.5 rounded-lg hover:bg-blue-50">
                    <Edit2 className="w-4 h-4" /><span className="text-sm">{t.edit}</span>
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* 新增/编辑弹窗 */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
            <div className="px-6 py-5 border-b flex items-center space-x-3">
              <div className="bg-blue-100 p-2 rounded-lg">
                <Store className="w-5 h-5 text-blue-600" />
              </div>
              <h3 className="text-lg font-bold">{editingStore ? t.editStoreTitle : t.addBranchBtn}</h3>
            </div>

            <div className="p-6 space-y-6 overflow-y-auto">

              {/* 分区块一：门店名称（各语言） */}
              <div>
                <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">{t.sectionNamesLabel}</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t.storeNameZhLabel} <span className="text-red-500">*</span></label>
                    <input type="text" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder={t.storeNamePlaceholder} />
                  </div>
                  {visibleLangFields.map(lang => (
                    <div key={lang.code}>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t.storeNameLangLabel.replace('{lang}', t[lang.labelKey])}
                      </label>
                      <input type="text" value={form[lang.field]}
                        onChange={e => setForm({ ...form, [lang.field]: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        placeholder={lang.code === 'en' ? t.englishNamePlaceholder : ''} />
                    </div>
                  ))}
                </div>
              </div>

              <div className="border-t border-gray-100" />

              {/* 分区块二：联系方式与位置 */}
              <div>
                <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">{t.sectionContactLabel}</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t.addressLabel}</label>
                    <input type="text" value={form.address} onChange={e => setForm({ ...form, address: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder={t.addressPlaceholder} />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t.phoneLabel}</label>
                    <input type="text" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder={t.phonePlaceholder} />
                  </div>
                </div>
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t.plusCodeLabel}
                    <span className="text-gray-400 text-xs font-normal ml-1">（{t.optionalLabel}）</span>
                  </label>
                  <input type="text" value={form.plus_code}
                    onChange={e => setForm({ ...form, plus_code: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder={t.plusCodePlaceholder} />
                  <p className="text-xs text-gray-400 mt-1">{t.plusCodeHint}</p>
                </div>
              </div>

              <div className="border-t border-gray-100" />

              {/* 分区块三：运营设置 */}
              <div>
                <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">{t.sectionOperationsLabel}</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {isBranch && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">{t.commissionRateLabel}</label>
                      <div className="relative">
                        <input type="number" min="0" max="100" step="0.1"
                          value={form.commission_rate}
                          onChange={e => setForm({ ...form, commission_rate: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 pr-10"
                          placeholder="5.0" />
                        <span className="absolute right-3 top-2.5 text-gray-400">%</span>
                      </div>
                      <p className="text-xs text-gray-400 mt-1">{t.commissionRateHint}</p>
                    </div>
                  )}
                  {editingStore && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">{t.statusLabel}</label>
                      <select value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500">
                        <option value="active">{t.activeStatus}</option>
                        <option value="inactive">{t.closedStatus}</option>
                      </select>
                    </div>
                  )}
                </div>
                {isBranch && (
                  <p className="text-xs text-gray-400 mt-3">{t.commissionRateExample}</p>
                )}
                {/* 是否支持到店自提——仓库类门店（纯粹囤货/中转用，不接待客户上门）
                    可以关闭这个开关，H5客户端选自提门店的时候不会出现这个门店 */}
                <label className="flex items-center gap-2 cursor-pointer mt-4 pt-4 border-t border-gray-100">
                  <input type="checkbox" checked={!!form.supports_pickup}
                    onChange={e => setForm({ ...form, supports_pickup: e.target.checked })}
                    className="w-4 h-4 rounded" />
                  <span className="text-sm text-gray-700">{t.supportsPickupLabel}</span>
                  <span className="text-xs text-gray-400">{t.supportsPickupHint}</span>
                </label>
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg text-sm">{error}</div>
              )}
            </div>

            <div className="px-6 py-4 border-t flex space-x-3 flex-shrink-0">
              <button onClick={() => setShowModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50">
                {t.cancel}
              </button>
              <button onClick={handleSave} disabled={saving}
                className={`flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg ${saving ? 'opacity-50 cursor-not-allowed' : 'hover:bg-blue-700'}`}>
                {saving ? t.savingBtn : t.save}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StoresPage;